import { get } from 'svelte/store';

import { DomainService } from '@client/services/domain';

import { appStore } from '@client/stores/app';
import { refreshDomainList } from '@client/stores/domain-list';
import { isDomainSwitching } from '@client/stores/domain-settings';
import { processUserConfig, userStore } from '@client/stores/user';

import { LocalStorage } from '@client/utils/local-storage';

// The switch request is not cancellable, so a superseded one is ignored rather than aborted.
let seq = 0;

const setBusy = (busy) => appStore.update((state) => ({ ...state, loading: busy, blocking: busy }));

/**
 * @param {string} domainname
 * @returns {Promise<{ok: boolean, domain: string, reason?: 'request-failed'|'not-eligible'|'superseded'}>}
 */
export const switchDomain = async (domainname) => {
  const id = ++seq;

  setBusy(true);
  isDomainSwitching.set(true); // Prevent domain settings from updating prematurely

  try {
    const data = await DomainService.switch(get(userStore).token, domainname);

    if (id !== seq) {
      return { ok: false, domain: domainname, reason: 'superseded' };
    }

    // Both guards run on the raw response: processUserConfig rewrites the token and persisted user.
    if (!data?.authEmail) {
      return { ok: false, domain: domainname, reason: 'request-failed' };
    }

    // The backend silently ignores an ineligible switch and returns the current domain's payload.
    if (data.authDomain !== domainname) {
      return { ok: false, domain: domainname, reason: 'not-eligible' };
    }

    const config = await processUserConfig(data);

    userStore.set(config);
    LocalStorage.update('domain', domainname);

    refreshDomainList();

    return { ok: true, domain: config.domain };
  } catch {
    return { ok: false, domain: domainname, reason: 'request-failed' };
  } finally {
    if (id === seq) {
      isDomainSwitching.set(false); // Allow domain settings to update now
      setBusy(false);
    }
  }
};
